import type { ChartResultRow } from '@/features/_shared/widget-registry';
import type { CatalogRow, ContentPart, StreamEvent } from '../types/chat.types';

export function parseStreamLine(line: string): StreamEvent | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  if (!trimmed.startsWith('data:')) throw new Error('Invalid stream line format');
  const json = trimmed.slice(5);
  const obj = JSON.parse(json);
  if (!obj || typeof obj.type !== 'string' || !('data' in obj))
    throw new Error('Invalid stream event');
  return obj;
}

type ToolResultEvent = Extract<StreamEvent, { type: 'tool_result' }>;
type Metadata = NonNullable<ToolResultEvent['data']['result']['metadata']>;

function metaStr(md: Metadata | undefined, key: string): string | undefined {
  const v = md?.[key];
  return typeof v === 'string' ? v : undefined;
}

function parseToolResult(evt: ToolResultEvent): ContentPart {
  const md = evt.data.result.metadata;
  const display = md?.display_type;
  const tool_call_id = evt.data.tool_call_id;
  if (display === 'table') {
    return {
      kind: 'tool_result',
      tool_call_id,
      display: 'table',
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      rows: (evt.data.result.results ?? []) as CatalogRow[],
      metadata: { start_date: md?.start_date, end_date: md?.end_date }
    };
  }
  if (display === 'default' || !display) {
    return {
      kind: 'tool_result',
      tool_call_id,
      display: 'default',
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      rows: (evt.data.result.results ?? []) as CatalogRow[],
      metadata: { start_date: md?.start_date, end_date: md?.end_date }
    };
  }
  if (display === 'spot_chart') {
    return {
      kind: 'tool_result',
      tool_call_id,
      display: 'spot_chart',
      title: metaStr(md, 'title'),
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      rows: (evt.data.result.results ?? []) as ChartResultRow[]
    };
  }
  if (display === 'seasonal_chart') {
    return {
      kind: 'tool_result',
      tool_call_id,
      display: 'seasonal_chart',
      title: metaStr(md, 'title'),
      from: metaStr(md, 'from'),
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      rows: (evt.data.result.results ?? []) as ChartResultRow[]
    };
  }
  if (display === 'forward_chart') {
    return {
      kind: 'tool_result',
      tool_call_id,
      display: 'forward_chart',
      title: metaStr(md, 'title'),
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      rows: (evt.data.result.results ?? []) as ChartResultRow[]
    };
  }
  if (display === 'file_chart') {
    const seriesFileName = metaStr(md, 'seriesFileName');
    const configFileName = metaStr(md, 'configFileName');
    if (!seriesFileName || !configFileName) {
      return {
        kind: 'notice',
        text: 'file_chart is missing required fields: seriesFileName, configFileName'
      };
    }
    return {
      kind: 'tool_result',
      tool_call_id,
      display: 'file_chart',
      title: metaStr(md, 'title'),
      seriesFileName,
      configFileName,
      queryFileId: metaStr(md, 'queryFileId'),
      downloadFileId: metaStr(md, 'downloadFileId'),
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      timeRangeButtons: md?.timeRangeButtons as
        | Array<{ label: string; value: string; default?: boolean }>
        | undefined
    };
  }
  if (display === 'static_chart') {
    return {
      kind: 'tool_result',
      tool_call_id,
      display: 'static_chart',
      title: metaStr(md, 'title'),
      chartConfig: evt.data.result.chart_config ?? {}
    };
  }
  return { kind: 'notice', text: `Unsupported display_type: ${display}` };
}

export function eventToContentPart(evt: StreamEvent): ContentPart | null {
  switch (evt.type) {
    case 'content':
      return { kind: 'text', text: evt.data.content };
    case 'tool_call':
      return { kind: 'tool_call', name: evt.data.name, args: evt.data.args, id: evt.data.id };
    case 'tool_result':
      return parseToolResult(evt);
    case 'complete':
      return { kind: 'notice', text: `Stream ${evt.data.status}` };
    default:
      throw new Error('Invalid stream event type');
  }
}
